import React, { useEffect, useState, useContext } from "react";
import "./Blog.css";
import axios from "axios";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import { API_BASE_URL, IMAGES_BASE_URL } from "../../api/config";
import CustomPagination from "../../components/Shared/CustomPagination";
import { useTranslateKey } from "../../components/translation/useTranslateKey";
import { LangContext } from "../../components/translation/langugeContext";

// Main Blog Component
const Blog = () => {
  const { lang } = useContext(LangContext);
  const t = useTranslateKey();
  const [blogPosts, setBlogs] = useState({});
  const [activeCategory, setActiveCategory] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const categories = [
    { name: t("allPosts"), value: "" },
    { name: t("marketUpdates"), value: "market_updates" },
    { name: t("investmentTips"), value: "investment_tips" },
    { name: t("propertyGuides"), value: "property_guides" },
    { name: t("lifestyle"), value: "lifestyle" },
  ];

  // Fetch data depending on page and categories
  const fetchBlogs = (page = 1, category = "") => {
    setIsLoading(true);
    let url = `${API_BASE_URL}/blogs/?page=${page}`;
    if (category) {
      url += `&category=${category}`;
    }

    axios
      .get(url, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then((response) => {
        console.log(response.data);
        setBlogs(response.data);
        setTotalPages(response.data.num_pages);
        setCurrentPage(response.data.current_page);
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        if (error.response) {
          console.log("Server responded:", error.response);
        } else if (error.request) {
          console.log("Request made but no response:", error.request);
        } else {
          console.log("Error setting up request:", error.message);
        }
      });
  };

  // First page download
  useEffect(() => {
    fetchBlogs(currentPage, activeCategory);
  }, [currentPage, activeCategory, lang]);

  // When changing category
  useEffect(() => {
    setCurrentPage(1); // Reset to first page
    fetchBlogs(1, activeCategory);
  }, [activeCategory]);

  // Handle page change from pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchBlogs(page, activeCategory);
  };

  return (
    <>
      <section id="articals" className="blogs-section container mx-auto px-4">
        {/* Blog Header */}
        <div className="text-center mb-16 mt-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-12">
            {t("latestNews")}
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            {t("subheading")}
          </p>
        </div>

        {/* Blog Categories */}
        <div className="flex flex-wrap justify-center gap-4 pt-3 pb-3">
          {categories.map((category) => (
            <button
              type="button"
              key={category.value}
              className={`px-6 py-2 rounded-full font-semibold transition-colors duration-200 ${
                activeCategory === category.value
                  ? "property-toggle-btn cursor-pointer rounded-lg text-sm font-medium flex transition-all duration-300 primary-active"
                  : "bg-primary-accent text-white hover:bg-accent-yellow hover:text-primary-darker"
              }`}
              onClick={() => setActiveCategory(category.value)}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Loader or Blog Posts */}

        {isLoading ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#11132F]">
            <div className="loader"></div>
          </div>
        ) : // Blog Grid or Message
        blogPosts.posts && blogPosts.posts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogPosts.posts.map((post) => (
              <div key={post.id} className="px-2">
                <BlogCard
                  imageUrl={post.image}
                  category={post.category}
                  category_ar={post.category_ar}
                  date={new Date(post.created_at).toLocaleDateString(
                    lang === "ar" ? "ar-EG" : "en-US",
                    {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    }
                  )}
                  readTime={post.read_time || 0}
                  title={
                    lang === "ar" ? post.title_ar || post.title : post.title
                  }
                  excerpt={
                    lang === "ar"
                      ? post.excerpt_ar || post.excerpt
                      : post.excerpt
                  }
                  id={post.id}
                />
              </div>
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-8">
                <CustomPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="col-span-full text-center text-white py-12">
            {t("noPosts")}
          </div>
        )}
      </section>
    </>
  );
};

// BlogCard Component
const BlogCard = ({
  imageUrl,
  category,
  category_ar,
  date,
  readTime,
  title,
  title_ar,
  excerpt,
  excerpt_ar,
  id,
}) => {
  const { lang } = useContext(LangContext);
  const t = useTranslateKey();

  return (
    <article className="blog-card">
      <div className="blog-image">
        <img src={`${IMAGES_BASE_URL}${imageUrl}`} alt={category} />
        {category ? (
          <span className="blog-category">
            {t(category)}{" "}
          </span>
        ) : null}
      </div>
      <div className="blog-content">
        <div className="blog-meta">
          <span>
            <i className="far fa-calendar rtl-margin2"></i> {date}
          </span>
          <span>
            <i className="far fa-clock rtl-margin2"></i> {readTime} {t("min")}
          </span>
        </div>
        <h2 className="blog-title">
          {lang === "ar" ? title_ar || title : title}
        </h2>
        <p className="blog-excerpt">
          {lang === "ar" ? excerpt_ar || excerpt : excerpt}
        </p>
        <div className="blog-footer">
          <a href={`/blog-details/${id}`} className="read-more">
            {t("readMore")}
            <i className="fas fa-arrow-right ml-2 arrow-icon"></i>
          </a>
        </div>
      </div>
    </article>
  );
};

export default Blog;
